(() => {
  const DOMAIN = window.__STOREFRONT_DOMAIN__ || window.location.hostname;
  const TOKEN = window.__STOREFRONT_TOKEN__;
  const API_VERSION = window.__STOREFRONT_API_VERSION__ || "2026-01";
  const ENDPOINT = `https://${DOMAIN}/api/${API_VERSION}/graphql.json`;

  const CACHE_TTL = 2 * 60 * 60 * 1000; // 2 hours
  const MAX_SIBLINGS = 60;

  if (!TOKEN) {
    console.warn("[siblings] Missing Storefront token");
    return;
  }

  const norm = (v) => (v ?? "").toString().trim();
  const normLower = (v) => norm(v).toLowerCase();

  function getCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts) return null;
      if (Date.now() - parsed.ts > CACHE_TTL) return null;
      return parsed.data;
    } catch {
      return null;
    }
  }

  function setCache(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch {}
  }

  async function gql(query, variables) {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (!res.ok || json.errors) {
      throw new Error(JSON.stringify(json.errors || res.statusText));
    }
    return json;
  }

  function buildQuery(metafieldKeys) {
  const mfLines = metafieldKeys
    .map((k) => `${k}: metafield(namespace: "custom", key: "${k}") { value }`)
    .join("\n");

  return `
    query Siblings($first: Int!, $after: String, $q: String!, $filters: [ProductFilter!]) {
      search(
        first: $first
        after: $after
        query: $q
        types: [PRODUCT]
        productFilters: $filters
      ) {
        pageInfo { hasNextPage endCursor }
        totalCount
        edges {
          node {
            ... on Product {
              handle
              availableForSale
              ${mfLines}
            }
          }
        }
      }
    }
  `;
}

  function parseProducts(json, metafieldKeys) {
  const edges = json?.data?.search?.edges || [];
  return edges
    .map(({ node }) => {
      if (!node || !node.handle) return null; // safety
      const mf = {};
      for (const k of metafieldKeys) mf[k] = norm(node?.[k]?.value);
      return {
        handle: node.handle,
        url: `/products/${node.handle}`,
        available: !!node.availableForSale,
        metafields: mf,
      };
    })
    .filter(Boolean);
}
  function uniqueOptions(siblings, key) {
    const m = new Map();
    for (const s of siblings) {
      const raw = norm(s.metafields[key]);
      const n = raw.toLowerCase();
      if (!raw) continue;
      if (n === "na" || n === "n/a") continue;
      if (!m.has(n)) m.set(n, raw);
    }
    // stable sort by display
    return Array.from(m.entries())
      .map(([n, display]) => ({ n, display }))
      .sort((a, b) => a.display.localeCompare(b.display, undefined, { sensitivity: "base" }));
  }

  function bestMatch(siblings, current, targetKey, targetNorm, allKeys) {
    let best = null;
    let bestScore = -1;

    for (const cand of siblings) {
      const candNorm = normLower(cand.metafields[targetKey]);
      if (candNorm === "na" || candNorm === "n/a") continue;
      if (candNorm !== targetNorm) continue;

      let score = 0;
      for (const k of allKeys) {
        if (k === targetKey) continue;
        const cur = normLower(current.metafields[k]);
        const cv = normLower(cand.metafields[k]);
        if (!cur || cur === "na" || cur === "n/a") continue;
        if (!cv || cv === "na" || cv === "n/a") continue;
        if (cur === cv) score++;
      }

      if (score > bestScore) {
        bestScore = score;
        best = cand;
      }
    }
    return best;
  }

  async function hydrate(widget) {
    const groupKey = widget.dataset.groupKey;
    const currentHandle = widget.dataset.currentHandle;
    if (!groupKey || !currentHandle) return;

    const cfgScript = widget.parentElement?.querySelector("[data-sibling-config]");
    const cfg = cfgScript ? JSON.parse(cfgScript.textContent) : { config: [] };

    // Only include config rows that are enabled by relations
    const relations = widget.dataset.relations || "";
    const enabled = (cfg.config || []).filter((row) => relations.includes(`|${row.rel}|`));

    // metafields we must request
    const keys = enabled.map((r) => r.key);

   // fetch siblings (paginated)
const cacheKey = `siblings:${groupKey}`;
let siblings = getCache(cacheKey);

if (!siblings) {
  const query = buildQuery(keys);

  let after = null;
  let all = [];
  const pageSize = Math.min(50, MAX_SIBLINGS);

  while (all.length < MAX_SIBLINGS) {
    const variables = {
      first: pageSize,
      after,
      q: "*",
      filters: [
        { productMetafield: { namespace: "custom", key: "group_key", value: groupKey } }
      ],
    };

    const json = await gql(query, variables);

    const batch = parseProducts(json, keys);
    all = all.concat(batch);

    const pageInfo = json?.data?.search?.pageInfo;
    if (!pageInfo?.hasNextPage) break;
    after = pageInfo.endCursor;
    if (!after) break;
  }

  // de-dupe by handle just in case
  const seen = new Set();
  siblings = all.filter(p => (seen.has(p.handle) ? false : (seen.add(p.handle), true)));

  setCache(cacheKey, siblings);
}
    

    const current = siblings.find((s) => s.handle === currentHandle) || { metafields: {} };

    // render each group
    const allKeys = keys;
    const groups = widget.querySelectorAll(".sibling-group[data-value-key]");

    groups.forEach((groupEl) => {
      const key = groupEl.getAttribute("data-value-key");
      const itemsEl = groupEl.querySelector("[data-items]");
      if (!key || !itemsEl) return;

      const opts = uniqueOptions(siblings, key);
      if (opts.length <= 1) {
        groupEl.style.display = "none";
        return;
      }
      groupEl.style.display = "";

      const curRaw = norm(current.metafields[key]);
      const curNorm = curRaw.toLowerCase();

      itemsEl.innerHTML = "";

      if (!curNorm || curNorm === "na" || curNorm === "n/a") {
        const span = document.createElement("span");
        span.className = "sibling-item is-active";
        span.textContent = "—";
        itemsEl.appendChild(span);
      }

      for (const opt of opts) {
        if (curNorm && opt.n === curNorm) {
          const span = document.createElement("span");
          span.className = "sibling-item is-active";
          span.textContent = opt.display;
          itemsEl.appendChild(span);
        } else {
          const best = bestMatch(siblings, current, key, opt.n, allKeys);
          if (!best) continue;
          const a = document.createElement("a");
          a.className = "sibling-item";
          a.href = best.url;
          a.textContent = opt.display;
          itemsEl.appendChild(a);
        }
      }
    });
    
  }

  function init() {
    document.querySelectorAll("[data-sibling-widget]").forEach((w) => {
      hydrate(w).catch((e) => console.error("[siblings] hydrate failed", e));
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
